import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import { useMediaQuery } from 'react-responsive';
import { useModal } from '@ebay/nice-modal-react';
import { withAsyncBoundary } from '@toss/async-boundary';
import { isSupportedCountryCode, type SupportedCountryCode } from '@zep-us/i18n';
import { useZepetofulSuspenseSchoolContent } from '@/api/zepetoful.api';
import { NoSSR } from '@/components';
import { joinSemesterTree } from '@/shared/category/util/joinSemesterTree';
import { DelayedLoadingFallback } from '@/shared/components';
import { useIsInLti } from '@/shared/lti';
import { LtiNavigationBar } from '@/shared/lti/components/LtiNavigationBar';
import { useUserPlanManager } from '@/shared/plan';
import { QUIZ_TYPE_PLAN_FEATURE } from '@/shared/plan/consts';
import { type PlanType, type QuizSet } from '@/types';
import { cn } from '@/utils/tailwind.util';
import { BuildInfoProvider, useBuildInfoContext } from '@/views/build/context/BuildInfoProvider';
import { AiBuilderChatPanel } from '@/views/build/features/ai-builder-chat/components/AiBuilderChatPanel';
import { AiBuilderPageMorphFrame } from '@/views/build/features/ai-builder-chat/components/AiBuilderPageMorphFrame';
import { AI_BUILDER_CHAT_DRAWER_BREAKPOINT, getAiBuilderEffectivePageSurface } from '@/views/build/features/ai-builder-chat/components/AiBuilderShellTransition';
import { useAiBuilderChatAvailability, useAiBuilderShellTransition } from '@/views/build/features/ai-builder-chat/hooks';
import { AiBuilderChatProvider, useAiBuilderChatActions, useAiBuilderChatPanelSelector } from '@/views/build/features/ai-builder-chat/stores';
import { SafeEnterTracking } from '@/views/build/features/enter-tracking';
import { BuildFormSubscription } from './components/BuildFormSubscription';
import { BuildPageErrorFallback } from './components/BuildPageErrorFallback';
import { BuildPageInitializer } from './components/BuildPageInitializer';
import { QuizListProvider } from './components/useQuizList';
import { useBuildStep } from './header/useBuildStep';
import { useBuildQuizsetForm } from './hook/useBuildQuizsetForm';
import { useBuildInfoQuery } from './hooks/useBuildInfoQuery';
import { resolveMapTemplateAvailability } from './utils/resolveMapTemplateAvailability';
import { resolveRestrictedProQuizzes, type RestrictedProQuiz } from './utils/resolveRestrictedQuizContent';
import { BuildHeader, BuildLoading, BuildPageBody } from './components';
import { DisabledEditAlertModal } from './modal';
import { transformMapTemplate } from './util';
type BuildProps = {
  isUpdate: boolean;
  updateData?: QuizSet;
  isDuplicate?: boolean;
};
export const BuildPageSuspense = (props: BuildProps) => {
  const {
    updateData
  } = props;
  const {
    data
  } = useBuildInfoQuery();
  const {
    data: selectionSchoolInfo
  } = useZepetofulSuspenseSchoolContent();
  const actionType = (() => {
    if (props.isUpdate) return 'UPDATE';
    if (props.isDuplicate) return 'DUPLICATE';
    return 'CREATE';
  })();
  const mapTemplates = useMemo(() => {
    return transformMapTemplate(data.data.templates);
  }, [data.data.templates]);
  const {
    userPlanManager
  } = useUserPlanManager();
  const mapTemplateAvailability = resolveMapTemplateAvailability({
    templateId: props.updateData?.templateId,
    visibleMapTemplates: mapTemplates,
    originMapTemplate: props.updateData,
    hasEnoughPlanType: planType => userPlanManager.hasEnoughPlanType(planType as PlanType)
  });
  const isAvailableMapTemplate = mapTemplateAvailability === 'available-visible' || mapTemplateAvailability === 'available-original';
  const hasProAccess = props.isUpdate && Object.values(QUIZ_TYPE_PLAN_FEATURE).every(feature => userPlanManager.canAccessFeature(feature));
  const restrictedProQuizzes = useMemo(() => props.isUpdate ? resolveRestrictedProQuizzes({
    quizList: updateData?.quizList ?? [],
    hasProAccess
  }) : [], [props.isUpdate, updateData?.quizList, hasProAccess]);
  const form = useBuildQuizsetForm({
    quizSetData: props.updateData,
    quizSetInfo: data.data,
    actionType,
    selectionSchoolInfo
  });

  // 국가 검사는 '다른 나라 교육과정으로 만들어진 기존 퀴즈를 그대로 고치는' UPDATE 에만
  // 적용한다. 복사(DUPLICATE)는 내 퀴즈를 새로 만드는 동작이고, 국가가 다르면 학교 정보를
  // 비워 내 국가 기준으로 다시 입력받으므로(useBuildQuizsetForm 의 isInitialize) 원본 국가가
  // 달라도 막을 이유가 없다. 서버도 복사 시 원본 국가를 검사하지 않는다.
  const isCountryMatch = actionType !== 'UPDATE' || data.data.userBuildInfo.countryCode.toLowerCase() === updateData?.countryCode?.toUpperCase().toLowerCase();
  const canEditQuiz = isCountryMatch;
  useEffect(() => {
    if (props.updateData) {
      // 업데이트 상황에서는 항상 설정, 중복이지만 템플릿이 가능할 경우는 설정가능
      if (props.isUpdate) {
        form.setValue('templateId', props.updateData.templateId);
        form.setValue('templateType', props.updateData.templateType);
        return;
      }
      if (props.isDuplicate && isAvailableMapTemplate) {
        form.setValue('templateId', props.updateData.templateId);
        form.setValue('templateType', props.updateData.templateType);
      }
    }
  }, [form, props.updateData, props.isUpdate, props.isDuplicate, isAvailableMapTemplate]);
  return <NoSSR data-sentry-element="NoSSR" data-sentry-component="BuildPageSuspense" data-sentry-source-file="BuildPageView.tsx">
      <BuildInfoProvider action={actionType} canEditQuiz={canEditQuiz} publishers={selectionSchoolInfo?.publishers} originQuizSet={props.updateData} mapTemplates={mapTemplates} schoolInfoTree={joinSemesterTree(data.data.categoryTree.schoolInfoTree, data.data.userBuildInfo.countryCode)} subjectTree={data.data.categoryTree.subjectTree} userCountryCode={isSupportedCountryCode(data.data.userBuildInfo.countryCode?.toUpperCase()) ? data.data.userBuildInfo.countryCode?.toUpperCase() as SupportedCountryCode : 'US'} quizBankExposedCategoryPrefixes={data.data.quizBank.exposedCategoryPrefixes} data-sentry-element="BuildInfoProvider" data-sentry-source-file="BuildPageView.tsx">
        <FormProvider {...form} data-sentry-element="FormProvider" data-sentry-source-file="BuildPageView.tsx">
          <BuildPageInitializer data-sentry-element="BuildPageInitializer" data-sentry-source-file="BuildPageView.tsx" />
          <SafeEnterTracking eventName="enter_build" data-sentry-element="SafeEnterTracking" data-sentry-source-file="BuildPageView.tsx">
            <BuildPageViewComponent {...props} canEditQuiz={canEditQuiz} restrictedMap={!isAvailableMapTemplate} restrictedProQuizzes={restrictedProQuizzes} data-sentry-element="BuildPageViewComponent" data-sentry-source-file="BuildPageView.tsx" />
          </SafeEnterTracking>
        </FormProvider>
      </BuildInfoProvider>
    </NoSSR>;
};
type BuildPageViewComponentProps = BuildProps & {
  /** 저장 버튼(BuildHeaderSubmitButton)이 보는 값과 같아야 한다 — 갈리면 안내 없이 저장만 막힌다. */
  canEditQuiz: boolean;
  restrictedMap: boolean;
  restrictedProQuizzes: RestrictedProQuiz[];
};
const BuildPageViewComponent = ({
  isUpdate,
  updateData,
  canEditQuiz,
  restrictedMap,
  restrictedProQuizzes
}: BuildPageViewComponentProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    action
  } = useBuildInfoContext();
  const isInLti = useIsInLti();
  const {
    show: showDisabledEditModal
  } = useModal(DisabledEditAlertModal);
  const {
    ref: inViewRef,
    inView
  } = useInView({
    threshold: 0,
    rootMargin: '0px 0px 0px 0px'
  });
  const {
    step,
    handleStepChange,
    isSubmitButtonShown,
    focusQuizField
  } = useBuildStep();

  // 진입 잠금 안내. "퀴즈 수정" 이 설정 탭(`?initialStep=settings`)에서도 문제 탭으로
  // 전환해 포커스해야 하므로, 탭 상태(useBuildStep)를 가진 이 컴포넌트가 띄운다.
  useEffect(() => {
    if (!updateData) {
      return;
    }
    if (!canEditQuiz) {
      void showDisabledEditModal({
        type: 'unknown'
      });
      return;
    }
    if (!isUpdate) {
      return;
    }
    if (restrictedMap || restrictedProQuizzes.length > 0) {
      void showDisabledEditModal({
        type: 'plan',
        restrictedMap,
        restrictedQuizzes: restrictedProQuizzes,
        onEdit: firstRestrictedQuiz => {
          if (firstRestrictedQuiz) {
            focusQuizField(`quizList.${firstRestrictedQuiz.index}.question`);
          }
        }
      });
    }
  }, [canEditQuiz, focusQuizField, isUpdate, restrictedMap, restrictedProQuizzes, showDisabledEditModal, updateData]);
  if (isLoading) return <BuildLoading />;

  // React 18.3.1 에서는 boolean inert 가 동작하지 않아 빈 문자열 속성 핵으로
  // 저장 중 빌더 전체 상호작용을 막는다. NiceModal/alertDialog 는 document.body
  // 로 portal 되므로 이 inert 의 영향을 받지 않는다.
  // TODO(React 19): inert={isSubmitting} 로 정리.
  const inertProps = (isSubmitting ? {
    inert: ''
  } : {}) as unknown as React.HTMLAttributes<HTMLDivElement>;
  return <div {...inertProps} data-sentry-component="BuildPageViewComponent" data-sentry-source-file="BuildPageView.tsx">
      {isInLti && <LtiNavigationBar currentPageKey="lti.nav.createQuiz" />}
      <BuildFormSubscription enabled={action === 'CREATE'} data-sentry-element="BuildFormSubscription" data-sentry-source-file="BuildPageView.tsx" />
      {/*
       * QuizListProvider와 AiBuilderChatProvider를 header/body 공통 레벨로 hoist.
       * AI builder 전환은 header까지 포함한 build page surface를 shell rect로 축소해야 한다.
       * useFieldArray('quizList')와 카드/사이드바 인스턴스는 step 토글과 AI panel 전환에도 보존한다.
       */}
      <QuizListProvider data-sentry-element="QuizListProvider" data-sentry-source-file="BuildPageView.tsx">
        <AiBuilderChatProvider data-sentry-element="AiBuilderChatProvider" data-sentry-source-file="BuildPageView.tsx">
          <BuildPageSurface step={step} onStepChange={handleStepChange} onFocusQuizField={focusQuizField} onSubmitQuiz={() => {
          setIsLoading(true);
        }} onSubmittingChange={setIsSubmitting} isBuildPageBodyInView={inView} isSubmitButtonShown={isSubmitButtonShown} inViewRef={inViewRef} data-sentry-element="BuildPageSurface" data-sentry-source-file="BuildPageView.tsx" />
        </AiBuilderChatProvider>
      </QuizListProvider>
    </div>;
};
type BuildPageSurfaceProps = {
  step: ReturnType<typeof useBuildStep>['step'];
  onStepChange: ReturnType<typeof useBuildStep>['handleStepChange'];
  onFocusQuizField: ReturnType<typeof useBuildStep>['focusQuizField'];
  onSubmitQuiz: () => void;
  onSubmittingChange: (isSubmitting: boolean) => void;
  isBuildPageBodyInView: boolean;
  isSubmitButtonShown: boolean;
  inViewRef: ReturnType<typeof useInView>['ref'];
};
const BuildPageSurface = ({
  step,
  onStepChange,
  onFocusQuizField,
  onSubmitQuiz,
  onSubmittingChange,
  isBuildPageBodyInView,
  isSubmitButtonShown,
  inViewRef
}: BuildPageSurfaceProps) => {
  const {
    panelOpen
  } = useAiBuilderChatPanelSelector();
  const {
    closePanel
  } = useAiBuilderChatActions();
  const {
    canUseAiBuilderChat
  } = useAiBuilderChatAvailability();
  const shouldOpenAiBuilderShell = panelOpen && canUseAiBuilderChat;
  const usesDrawerLayout = useMediaQuery({
    maxWidth: AI_BUILDER_CHAT_DRAWER_BREAKPOINT
  });
  const {
    pageSurfaceState,
    pageSurfaceMode,
    chatPanelTransitionState
  } = useAiBuilderShellTransition({
    // 모바일 body scroll lock은 Vaul/Radix modal에 위임한다.
    shouldLockBodyScroll: !usesDrawerLayout,
    shouldOpen: shouldOpenAiBuilderShell
  });
  const effectivePageSurface = getAiBuilderEffectivePageSurface({
    mode: pageSurfaceMode,
    state: pageSurfaceState,
    usesDrawerLayout
  });
  useEffect(() => {
    if (panelOpen && !canUseAiBuilderChat) {
      closePanel();
    }
  }, [canUseAiBuilderChat, closePanel, panelOpen]);
  return <div className={cn('min-h-dvh', effectivePageSurface.state === 'page' ? 'bg-neutral' : 'bg-gray-200')} data-sentry-component="BuildPageSurface" data-sentry-source-file="BuildPageView.tsx">
      <AiBuilderPageMorphFrame state={effectivePageSurface.state} data-sentry-element="AiBuilderPageMorphFrame" data-sentry-source-file="BuildPageView.tsx">
        <BuildHeader step={step} onStepChange={onStepChange} onFocusQuizField={onFocusQuizField} onSubmitQuiz={onSubmitQuiz} onSubmittingChange={onSubmittingChange} isBuildPageBodyInView={isBuildPageBodyInView} isSubmitButtonShown={isSubmitButtonShown} surfaceMode={effectivePageSurface.mode} data-sentry-element="BuildHeader" data-sentry-source-file="BuildPageView.tsx" />

        <div ref={inViewRef} className="pointer-events-none absolute left-0 top-0 h-xs w-full" aria-hidden="true" />
        <BuildPageBody currentStep={step} surfaceMode={effectivePageSurface.mode} data-sentry-element="BuildPageBody" data-sentry-source-file="BuildPageView.tsx" />
      </AiBuilderPageMorphFrame>

      {chatPanelTransitionState && <AiBuilderChatPanel transitionState={chatPanelTransitionState} />}
    </div>;
};
export const BuildPageView = withAsyncBoundary(BuildPageSuspense, {
  pendingFallback: <DelayedLoadingFallback delayMS={250}>
      <BuildLoading />
    </DelayedLoadingFallback>,
  rejectedFallback: ({
    error,
    reset
  }) => <BuildPageErrorFallback error={error} reset={reset} />
});